import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9 } from "styled-components";
import { hasItems } from "~/features/app/utils";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { takeFirst } from "~/features/app/utils";
import { Grid } from "~/features/ui/grid";
import { Headline } from "~/features/ui/headline";
import { LinkListLink, type LinkListLinkViewProps } from "~/features/blocks/inventory/link-list/link-list-link";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type LinkListBlockViewProps<TItem = LinkListLinkViewProps> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  getItemKey?: (item: TItem, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  items?: TItem[] | null;
  mode?: string | null;
  renderItem: (item: TItem, index: number) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function LinkListBlockView<TItem = LinkListLinkViewProps>({
  anchor,
  getItemKey,
  hasTopDivider = true,
  headline,
  items = [],
  mode = "light",
  renderItem,
  sectionProps,
  theme = "white"
}: LinkListBlockViewProps<TItem>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const entries = items ?? [];
  if (!hasItems(entries)) {
    return null;
  }
  return <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[{
    "position": "relative",
    "zIndex": "10",
    "backgroundColor": "var(--bg-secondary)"
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }]}>
      <_StyledWrap>
        <Grid>
          <_StyledDiv $_css2={[{
          "gridColumn": "1 / -1",
          "display": "flex",
          "flexDirection": "column",
          "@media (min-width: 1024px)": {
            "gridColumn": "span 4 / span 4"
          }
        }]}>
            {headline && <_StyledDiv2 $_css3={[{
            "borderBottomWidth": "1px",
            "borderColor": "var(--stroke-primary)",
            "padding": "4rem",
            "@media (min-width: 1024px)": {
              "marginLeft": "4rem",
              "borderWidth": "0px",
              "paddingTop": "6.4rem",
              "paddingBottom": "6.4rem"
            }
          }, themeStyle.border]}>
                <_StyledHeadline type="h2" text={headline} />
              </_StyledDiv2>}
            <_StyledDiv3 />
          </_StyledDiv>
          <_StyledDiv4>
            <_StyledUl>
              {entries.map((item, idx) => <_StyledLi key={getItemKey?.(item, idx) || idx} $_css4={[{
              "paddingLeft": "4rem",
              "paddingRight": "4rem",
              "paddingBottom": "2.4rem",
              "&:not(:last-child)": {
                "borderBottomWidth": "1px"
              },
              "@media (min-width: 1024px)": {
                "borderBottomWidth": "1px",
                "paddingLeft": "0",
                "paddingRight": "0",
                "paddingBottom": "4rem"
              }
            }, themeStyle.border]}>
                  {renderItem(item, idx)}
                </_StyledLi>)}
            </_StyledUl>
          </_StyledDiv4>
        </Grid>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-1vh6z52-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-1vh6z52-1"
})({
  "borderWidth": "0px"
});
var _StyledDiv = _styled3("div").withConfig({
  componentId: "sc-sf-1vh6z52-2"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-1vh6z52-3"
})`${p => p.$_css3}`;
var _StyledHeadline = _styled5(Headline).withConfig({
  componentId: "sc-sf-1vh6z52-4"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledDiv3 = _styled6("div").withConfig({
  componentId: "sc-sf-1vh6z52-5"
})({
  "display": "none",
  "padding": "8rem",
  "@media (min-width: 1024px)": {
    "display": "block"
  }
});
var _StyledDiv4 = _styled7("div").withConfig({
  componentId: "sc-sf-1vh6z52-6"
})({
  "gridColumn": "1 / -1",
  "paddingBottom": "0",
  "paddingTop": "var(--spacing-md)",
  "@media (min-width: 1024px)": {
    "gridColumn": "span 8 / span 8",
    "padding": "4rem"
  }
});
var _StyledUl = _styled8("ul").withConfig({
  componentId: "sc-sf-1vh6z52-7"
})({
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "2.4rem",
  "@media (min-width: 1024px)": {
    "rowGap": "4rem"
  }
});
var _StyledLi = /*#__PURE__*/_styled9("li").withConfig({
  displayName: "link-list___StyledLi",
  componentId: "sc-1mc5xvf-0"
})(["", ""], p => p.$_css4);